export interface MultiClaimResultGroup<T extends Record<string, any>> {
    claim_number: number;
    child_task_id?: string;
    claim_status?: string;
    success: boolean;
    compare_results: Array<T & {
        claim_number: number;
        child_task_id?: string;
        claim_status?: string;
        success: boolean;
    }>;
}

export const normalizeMultiClaimResultGroups = <T extends Record<string, any>>(
    content: any,
    normalizeClaimResult: (claimResult: any) => T[]
): MultiClaimResultGroup<T>[] => {
    if (!Array.isArray(content?.claim_results)) return [];

    return content.claim_results
        .map((claimResult: any) => {
            const claimNumber = Number(claimResult?.claim_number);
            const success = claimResult?.status === 'SUCCESS';
            return {
                claim_number: claimNumber,
                child_task_id: claimResult?.child_task_id,
                claim_status: claimResult?.status,
                success,
                compare_results: normalizeClaimResult(claimResult).map(result => ({
                    ...result,
                    claim_number: claimNumber,
                    child_task_id: claimResult?.child_task_id,
                    claim_status: claimResult?.status,
                    success,
                })),
            };
        })
        .filter(group => Number.isFinite(group.claim_number));
};

export const flattenMultiClaimResultGroups = <T extends Record<string, any>>(
    groups: MultiClaimResultGroup<T>[]
) => groups.flatMap(group => group.compare_results);

export type BlueHistoryKind = 'legacy' | 'multi' | 'none';
export type CompareStage = 'blue' | 'red' | 'judge';
export type StageHistoryKind = 'legacy' | 'multi' | 'none';

const hasLegacyStageResult = (content: any, stage: CompareStage) => {
    if (stage === 'judge') {
        return Array.isArray(content?.final_result?.all_mappings);
    }
    if (stage === 'red') {
        return Array.isArray(content?.blue_result?.all_mappings) && (
            Array.isArray(content?.red_result?.all_mappings) ||
            Array.isArray(content?.red_result?.mapping_challenges)
        );
    }

    return Array.isArray(content?.blue_result?.all_mappings);
};

export const getStageHistoryKind = (
    content: any,
    stage: CompareStage
): StageHistoryKind => {
    if (content?.stage_status?.[stage] !== 'SUCCESS') return 'none';

    if (Object.prototype.hasOwnProperty.call(content || {}, 'claim_results')) {
        const hasRenderableClaim = Array.isArray(content.claim_results) && content.claim_results.some(
            (claimResult: any) => hasLegacyStageResult(claimResult, stage)
        );
        return hasRenderableClaim ? 'multi' : 'none';
    }

    return hasLegacyStageResult(content, stage) ? 'legacy' : 'none';
};

export const getBlueHistoryKind = (content: any): BlueHistoryKind => {
    return getStageHistoryKind(content, 'blue');
};

export const getMultiClaimSelectionNumbers = (steps: any[] = []): number[] | null => {
    const claimSelectionStep = [...steps].reverse().find(
        step => step?.step_type === 'aicc_claim_selection' &&
            Array.isArray(step?.content?.independent_claims) &&
            step.content.independent_claims.some((claim: any) => (
                Object.prototype.hasOwnProperty.call(claim || {}, 'selected')
            ))
    );
    if (!claimSelectionStep) return null;

    return claimSelectionStep.content.independent_claims
        .filter((claim: any) => claim?.selected === true)
        .map((claim: any) => Number(claim?.claim_number))
        .filter((claimNumber: number) => Number.isFinite(claimNumber));
};

const getSelectedClaimNumbers = (steps: any[] = []): number[] => {
    const claimSelectionStep = [...steps].reverse().find(
        step => step?.step_type === 'aicc_claim_selection' && step?.content
    );
    if (!claimSelectionStep) return [];

    const { content } = claimSelectionStep;
    const modernSelection = getMultiClaimSelectionNumbers([claimSelectionStep]);
    if (modernSelection !== null) return modernSelection;

    const selectedClaims = Array.isArray(content.selected_claims)
        ? content.selected_claims
        : [content.selected_claim].filter(Boolean);
    return selectedClaims
        .map((claim: any) => Number(claim?.claim_number))
        .filter((claimNumber: number) => Number.isFinite(claimNumber));
};

const getHistoryStepTimestamp = (step: any) => Number(
    step?.updated_at ?? step?.content?.updated_at ?? step?.created_at ?? 0
);

/**
 * History may contain one unnumbered stage or one stage per Claim. Normalize all
 * valid records into claim_results without using their shape as a flow-version flag.
 */
export const buildMultiClaimStageHistoryContent = (
    steps: any[] = [],
    stage: CompareStage
) => {
    const selectedClaimNumbers = getSelectedClaimNumbers(steps);
    const latestStepByClaimKey = new Map<string, { claimNumber?: number; step: any }>();

    const registerClaimStep = (claimNumber: number, step: any) => {
        const hasClaimNumber = Number.isFinite(claimNumber);
        const claimKey = hasClaimNumber ? `claim:${claimNumber}` : 'claim:unnumbered';
        const existingStep = latestStepByClaimKey.get(claimKey)?.step;
        if (!existingStep || getHistoryStepTimestamp(step) >= getHistoryStepTimestamp(existingStep)) {
            latestStepByClaimKey.set(claimKey, {
                claimNumber: hasClaimNumber ? claimNumber : undefined,
                step,
            });
        }
    };

    steps.forEach((step) => {
        const content = step?.content;
        if (step?.step_type !== 'aicc_cc_compare_stage') return;

        const historyKind = getStageHistoryKind(content, stage);
        if (historyKind === 'multi') {
            content.claim_results.forEach((claimResult: any) => {
                if (!hasLegacyStageResult(claimResult, stage)) return;
                registerClaimStep(Number(claimResult?.claim_number), {
                    ...step,
                    content: claimResult,
                });
            });
            return;
        }
        if (historyKind !== 'legacy') return;

        registerClaimStep(Number(content?.claim_number), step);
    });

    const numberedEntries = [...latestStepByClaimKey.entries()]
        .filter(([claimKey]) => claimKey !== 'claim:unnumbered')
        .map(([, entry]) => entry);
    const unnumberedEntry = latestStepByClaimKey.get('claim:unnumbered');
    // A numbered per-Claim result supersedes an older unnumbered snapshot.
    const stageEntries = numberedEntries.length > 0
        ? numberedEntries
        : [unnumberedEntry].filter(Boolean) as Array<{ claimNumber?: number; step: any }>;
    if (stageEntries.length === 0) return null;

    const selectedOrder = new Map(selectedClaimNumbers.map((claimNumber, index) => [claimNumber, index]));
    stageEntries.sort((left, right) => {
        const leftOrder = left.claimNumber == null
            ? Number.MAX_SAFE_INTEGER
            : (selectedOrder.get(left.claimNumber) ?? Number.MAX_SAFE_INTEGER);
        const rightOrder = right.claimNumber == null
            ? Number.MAX_SAFE_INTEGER
            : (selectedOrder.get(right.claimNumber) ?? Number.MAX_SAFE_INTEGER);
        return leftOrder - rightOrder || getHistoryStepTimestamp(left.step) - getHistoryStepTimestamp(right.step);
    });

    if (stageEntries.length === 1 && stageEntries[0].claimNumber == null && selectedClaimNumbers.length > 0) {
        stageEntries[0].claimNumber = selectedClaimNumbers[0];
    }

    const claimResults = stageEntries.map(({ claimNumber, step }) => {
        const content = step.content;
        return {
            claim_number: claimNumber,
            child_task_id: content.child_task_id || content.task_id,
            status: content.status || content.stage_status?.[stage] || 'SUCCESS',
            stage: content.stage || `${stage.toUpperCase()}_DONE`,
            analysis_context: content.analysis_context,
            blue_result: content.blue_result,
            red_result: content.red_result,
            final_result: content.final_result,
        };
    });

    console.debug('[MultiClaimStageHistory] Aggregated per-claim stage history:', {
        stage,
        claimNumbers: claimResults.map(result => result.claim_number),
        claimCount: claimResults.length,
    });

    return {
        current_stage: stage,
        stage_status: { [stage]: 'SUCCESS' },
        claim_results: claimResults,
    };
};

export const buildMultiClaimBlueHistoryContent = (steps: any[] = []) => (
    buildMultiClaimStageHistoryContent(steps, 'blue')
);
